import {Box, Button, Dialog, DialogActions, DialogContent, useTheme} from "@mui/material";
import React, {useContext, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue} from "recoil";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import BootstrapDialogTitle from "../../../common/components/BootstrapDialogTitle";
import CustomStepper from "../../../common/components/CustomStepper";
import {excessGroupAtom, excessGroupCreationAtom, initialExcessGroupCreation} from "../../../state/excessGroupsRecoil";
import ExcessGroupAssociation from "./ASSOCIATION/ExcessGroupAssociation";
import ExcessGroupBaseDefinition from "./DEFINITION/ExcessGroupBaseDefinition";
import ExcessGroupMatrixDefinition from "./MATRIX/ExcessGroupMatrixDefinition";
import {BASE_DEFINITION_FORM_STEPS} from "../../../constants";
import DialogComponent from "../../../common/components/DialogComponent";
import {DialogInterface} from "../../../api/types/generic";
import {yupReservationFromFunc, yupReservationToFunc} from "../../../yupUtils";
import * as yup from "yup";
import {FormProvider, useForm} from "react-hook-form";
import {FormExcessGroupModel} from "../../../api/types/excessGroup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {FormExcessAssociationModel} from "../../../api/types/excess";

type ExcessGroupCreationFormProps = {
    isOpen: boolean;
    onDone: () => void;
    onClose: () => void;
    onStepChange?: (index: number) => void;
    refresh: Function;
    excessGroupInUsed: boolean;
};

const ExcessGroupFormContainer = ({
                                      isOpen,
                                      onClose,
                                      onStepChange,
                                      onDone,
                                      refresh,
                                      excessGroupInUsed
                                  }: ExcessGroupCreationFormProps) => {
    const [initialValues] = useState<FormExcessGroupModel>({
        commercialCategory: null,
        commercialSegmentId: null,
        name: "",
        validityDateFrom: "",
        validityDateTo: "",
        description: "",
        dealer: null,
        legalEntity: null
    });

    const [initialValuesAssociation] = useState<FormExcessAssociationModel>({
        carGroups: [],
        excesses: []
    });
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // use Theme
    const theme = useTheme();

    //use state
    const [dialog, setDialog] = useState<DialogInterface | null>(null);


    // // Use Recoil
    const [excessGroupCreation, setExcessGroupCreation] = useRecoilState(excessGroupCreationAtom);
    const {readOnly, completed, step, definitionData} = excessGroupCreation;
    const excessGroup = useRecoilValue(excessGroupAtom);

    const submitRef = useRef<HTMLButtonElement>(null);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    const currentStep = () => {
        switch (step) {
            case BASE_DEFINITION_FORM_STEPS.DEFINITION:
                return <ExcessGroupBaseDefinition submitRef={submitRef} refresh={refresh}
                                                  excessGroupInUsed={excessGroupInUsed}/>;
            case BASE_DEFINITION_FORM_STEPS.ASSOCIATION:
                return <FormProvider {...formAssociation}><ExcessGroupAssociation submitRef={submitRef}
                                                                                  refresh={refresh}/></FormProvider>;
            case BASE_DEFINITION_FORM_STEPS.MATRIX:
                return <ExcessGroupMatrixDefinition submitRef={submitRef}/>;
            case BASE_DEFINITION_FORM_STEPS.DONE:
                onDone();
                setExcessGroupCreation(initialExcessGroupCreation);
                return;
            default:
                return null;
        }
    };

    const validationSchema = () => {
        const yupReservationFrom = yupReservationFromFunc({
            t,
            id: excessGroup?.id || null,
            reservationFrom: excessGroup?.validityDateFrom as string,
        });
        const yupReservationTo = yupReservationToFunc({
            t,
            id: excessGroup?.id || null,
            reservationTo: excessGroup?.validityDateTo as string,
            reservationFromFieldName: "validityDateFrom"
        });

        return yup.object().shape({
            name: yup.string().required(t("translation:validation.fieldMandatory")).min(2),
            commercialCategory: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string()
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable(true),
            commercialSegmentId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string()
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            description: yup.string().required(t("translation:validation.fieldMandatory")),
            validityDateFrom: yupReservationFrom,
            validityDateTo: yupReservationTo
        })
            .defined();
    }

    // Use Form Context
    const form = useForm<FormExcessGroupModel>({
        defaultValues: definitionData || initialValues,
        resolver: yupResolver(
            validationSchema(),
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });

    const validationSchemaAssociation = yup
        .object()
        .shape({
            excesses: yup
                .array()
                .of(
                    yup.object().shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                )
                .required(t("translation:validation.fieldMandatory"))
                .min(1, t("translation:validation.fieldMandatory"))
                .nullable(true),
            carGroups: yup
                .array()
                .of(
                    yup.object().shape({
                        code: yup.string(),
                        label: yup.string()
                    })
                )
                .required(t("translation:validation.fieldMandatory"))
                .min(1, t("translation:validation.fieldMandatory"))
                .nullable(true)
        })
        .defined();

    // Use Form Context
    const formAssociation = useForm<FormExcessAssociationModel>({
        defaultValues: initialValuesAssociation,
        resolver: yupResolver(
            validationSchemaAssociation,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });

    const onSubmit = () => {
        submitRef?.current?.click();
    };

    const handleOnClose = () => {
        onClose();
        form.reset(initialValues)
        formAssociation.reset(initialValuesAssociation)
    };

    const saveAndQuit = async () => {
        if (step === 0) {
            const commercialCategory = await form.trigger("commercialCategory")
            const commercialSegmentId = await form.trigger("commercialSegmentId")
            const name = await form.trigger("name")
            const from = await form.trigger("validityDateFrom")
            const to = await form.trigger("validityDateTo")
            const description = await form.trigger("description")
            if (commercialCategory && commercialSegmentId && name && from && to && description) {
                onSubmit();
                handleOnClose();
            }
        } else if (step === 1) {
            const carGroups = await formAssociation.trigger("carGroups")
            const excesses = await formAssociation.trigger("excesses")
            if (carGroups && excesses) {
                onSubmit();
                handleOnClose();
            }
        }
    };

    const handleChangeStep = (clickedStep: number) => {
        if (clickedStep <= step || completed.slice(step, clickedStep).every((value) => value === true)) {
            if (readOnly) {
                onStepChange && onStepChange(clickedStep);
                setExcessGroupCreation({...excessGroupCreation, step: clickedStep});
            } else {
                setDialog({
                    open: true,
                    message: t("pricing:dialogs.messages.looseChanging"),
                    onClickConfirm: () => {
                        onStepChange && onStepChange(clickedStep);
                        setExcessGroupCreation({...excessGroupCreation, step: clickedStep});
                        setDialog(null);
                    }
                });
            }
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:steps.warningNextStep"),
                        type: snackbarTypes.warning,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    };

    return (
        <Dialog open={isOpen} maxWidth={"lg"} fullWidth>
            <BootstrapDialogTitle id="customized-dialog-title" onClose={handleOnClose}>
                <Box sx={{display: "flex", alignItems: "center", width: "100%"}}>
                    <CustomStepper
                        activeStep={step}
                        completed={completed}
                        steps={[
                            t("pricing:steps.definition"),
                            t("pricing:steps.association"),
                            t("pricing:steps.matrix")
                        ]}
                        handleStep={handleChangeStep}
                    />
                </Box>
            </BootstrapDialogTitle>
            <DialogContent style={{color: theme.palette.primary.main}}>
                <FormProvider {...form}>
                    {currentStep()}
                </FormProvider>
            </DialogContent>
            {!readOnly ? (
                <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                    <Button variant="outlined" color={"accent"} onClick={handleOnClose}>
                        {t("pricing:actions.cancel")}
                    </Button>
                    {step === BASE_DEFINITION_FORM_STEPS.MATRIX || (
                        <Button variant="outlined" color={"accent"} onClick={saveAndQuit}>
                            {t("pricing:actions.saveAndExit")}
                        </Button>
                    )}
                    <Button
                        sx={{marginLeft: "16px"}}
                        color={"accent"}
                        onClick={onSubmit}
                        onSubmit={onSubmit}
                    >
                        {step === BASE_DEFINITION_FORM_STEPS.MATRIX
                            ? t("pricing:actions.saveAndExit")
                            : t("pricing:actions.saveAndProceed")}
                    </Button>
                </DialogActions>
            ) : (
                <div style={{padding: "1rem"}}/>
            )}
            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => setDialog(null)}
                    open={dialog.open}
                    message={dialog.message}
                />
            }
        </Dialog>
    );
};

export default ExcessGroupFormContainer;
